import React, { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Alert, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import type { ModelComplexity } from "../../../functions/src/types";
import {
    checkoutDeadlineClosingHour,
    formatCheckoutDeadlineHourLabel,
    type BasketCheckoutUrgencyState,
} from "../../../functions/src/common/checkoutDeadlineUrgency";
import { formatBasketDayLabelsForMessage } from "../../../functions/src/common/orderDeadlines";
import { useBasketStrings } from "./basket-strings";

type ActiveUrgency = Extract<BasketCheckoutUrgencyState, { active: true }>;

type CheckoutDeadlineUrgencyAlertProps = {
    urgency: ActiveUrgency;
    variant: 'drawer' | 'compact';
    modelConstants: ModelComplexity | null;
    onOpenBasket: () => void;
    onDismiss?: () => void;
};

export default function CheckoutDeadlineUrgencyAlert({
    urgency,
    variant,
    modelConstants,
    onOpenBasket,
    onDismiss,
}: CheckoutDeadlineUrgencyAlertProps) {
    const strings = useBasketStrings();
    const daysLabel = formatBasketDayLabelsForMessage(urgency.orderDates);
    const hour = formatCheckoutDeadlineHourLabel(
        checkoutDeadlineClosingHour(urgency.earliest.kind, modelConstants),
    );
    const message = urgency.earliest.kind === 'set_menu'
        ? strings.deadlineUrgencySetMenu(daysLabel, hour)
        : strings.deadlineUrgencyGeneral(daysLabel, hour);

    const textRef = useRef<HTMLSpanElement>(null);
    const [singleLine, setSingleLine] = useState(false);

    useEffect(() => {
        const el = textRef.current;
        if (!el) {
            return;
        }
        const measure = (): void => {
            const style = getComputedStyle(el);
            const parsedLineHeight = parseFloat(style.lineHeight);
            const lineHeight = Number.isFinite(parsedLineHeight) ? parsedLineHeight : el.clientHeight;
            setSingleLine(el.scrollHeight <= lineHeight * 1.25);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [message]);

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpenBasket();
        }
    };

    const opensBasketOnActivate = variant !== 'drawer';

    return (
        <Alert
            severity="warning"
            icon={false}
            role="status"
            className={`checkout-deadline-urgency checkout-deadline-urgency--${variant}`}
            tabIndex={opensBasketOnActivate ? 0 : undefined}
            onClick={opensBasketOnActivate ? onOpenBasket : undefined}
            onKeyDown={opensBasketOnActivate ? handleKeyDown : undefined}
            aria-label={opensBasketOnActivate ? strings.deadlineUrgencyOpenBasketAria : undefined}
            action={onDismiss && variant !== 'drawer' ? (
                <IconButton
                    aria-label={strings.deadlineUrgencyDismissAria}
                    color="inherit"
                    size="small"
                    onClick={event => {
                        event.stopPropagation();
                        onDismiss();
                    }}
                >
                    <CloseIcon fontSize="inherit" />
                </IconButton>
            ) : undefined}
            sx={{
                display: 'grid',
                gridTemplateColumns: variant === 'drawer' ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) auto',
                alignItems: singleLine ? 'center' : 'start',
                ...(opensBasketOnActivate ? { cursor: 'pointer' } : {}),
                ...(variant === 'compact' ? { py: 0.25 } : {}),
                '& .MuiAlert-message': { display: 'block', padding: 0, minWidth: 0, width: '100%' },
                '& .MuiAlert-action': { gridColumn: 2, gridRow: 1, alignSelf: singleLine ? 'center' : 'start', padding: 0, margin: 0 },
            }}
        >
            <div className={`checkout-deadline-urgency-inner${singleLine ? " checkout-deadline-urgency-inner--single-line" : ""}`}>
                <WarningAmberOutlined className="checkout-deadline-urgency-inner__icon" aria-hidden />
                <span ref={textRef} className="checkout-deadline-urgency-text">{message}</span>
            </div>
        </Alert>
    );
}
